import type { MailboxInfo } from "owa-client-types";
import { QueryAnnotationType } from "@1js/sydney-types";
import type { QueryAnnotation } from "@1js/sydney-types";
import { detectSummarizeAnnotationIdTypeMismatch, logReconciledAnnotationIdType, } from "../telemetry/summarizeAnnotationIdTypeTelemetry";

/**
 * Reconciles each query annotation's `queryAnnotationType` with its id format before the
 * annotations are handed off to BizChat. Upstream resolution (notably SxS / secondary
 * reading pane / projected windows, which are common in Monarch) can tag a Message-typed
 * id (EWS prefix `AAMk`) as `EmailConversation`, or a Conversation-typed id (EWS prefix
 * `AAQk`) as `Message`. Sending that mislabeled pair makes GCI reject the request with
 * `ErrorInvalidIdMalformed`, so we correct the type to match the id and emit telemetry so
 * the frequency of the correction can be measured.
 *
 * Only unambiguous EWS-prefix mismatches are corrected; immutable/non-EWS ids are left
 * untouched (the detector returns `undefined` for them), so we never retag on a guess.
 * @param queryAnnotations - The annotations about to be sent to BizChat
 * @param mailboxInfo - The mailbox the annotations belong to (for telemetry context)
 * @param source - The entry point requesting summarization (for telemetry attribution)
 * @returns The annotations with any mismatched type corrected to match the id format
 */
export function reconcileQueryAnnotationIdTypes(queryAnnotations: QueryAnnotation[], mailboxInfo: MailboxInfo, source: string): QueryAnnotation[] {
    return queryAnnotations.map(annotation => {
        const mismatch = detectSummarizeAnnotationIdTypeMismatch(annotation.queryAnnotationType, annotation.id);
        
        if (!mismatch) {
            return annotation;
        }
        
        const correctedType = mismatch === "messageIdTaggedAsEmailConversation"
            ? QueryAnnotationType.Message
            : QueryAnnotationType.EmailConversation;
        
        logReconciledAnnotationIdType(mailboxInfo, source, mismatch);
        
        return {
            ...annotation,
            queryAnnotationType: correctedType,
            messageAnnotationMetadata: annotation.messageAnnotationMetadata
                ? { ...annotation.messageAnnotationMetadata, "@type": correctedType }
                : annotation.messageAnnotationMetadata,
        };
    });
}
